import React, { useState, ReactNode, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { DropdownItem } from '../../types/dropdown';
import './Dropdown.css';

interface DropdownProps {
  trigger: ReactNode;
  items: DropdownItem[];
  className?: string;
}

const MENU_WIDTH = 200;

const Dropdown: React.FC<DropdownProps> = ({ trigger, items, className }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const dropdownRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // The menu is portalled to document.body and positioned with viewport
  // coordinates, so it is never trapped by a transformed ancestor.
  const updateMenuPosition = () => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    setMenuPosition({
      top: rect.bottom + 4,
      left: rect.right - MENU_WIDTH // Align to right edge with menu width
    });
  };

  const handleTriggerClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();

    if (!isOpen) {
      updateMenuPosition();
    }

    setIsOpen(!isOpen);
  };

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  const handleItemClick = (item: DropdownItem) => (event: React.MouseEvent) => {
    event.stopPropagation();
    handleClose();
    item.onClick();
  };

  // Close dropdown when clicking outside. The menu lives outside dropdownRef
  // once portalled, so it has to be excluded explicitly - otherwise mousedown
  // on an item would unmount it before its onClick could fire.
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const insideTrigger = dropdownRef.current?.contains(target);
      const insideMenu = menuRef.current?.contains(target);
      if (!insideTrigger && !insideMenu) {
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, handleClose]);

  // Coordinates are captured once at open time, so they go stale as soon as the
  // trigger moves. Dismiss rather than chase it: a menu anchored to a card in a
  // scrolling list looks worse lagging the scroll than it does closing. Scroll
  // is captured so scrolling containers, not just the window, dismiss it too.
  //
  // Subscribing is deferred by a frame: focusing the trigger makes the browser
  // scroll it into view, and that scroll event lands just after the menu opens,
  // which would otherwise dismiss it instantly. The scroll itself is applied
  // before mousedown, so the position captured on click is already correct -
  // only the event is late. Scroll events are dispatched before animation frame
  // callbacks, so one frame skips it without missing any later scroll.
  useEffect(() => {
    if (!isOpen) return;

    let subscribed = false;
    const frame = window.requestAnimationFrame(() => {
      subscribed = true;
      window.addEventListener('scroll', handleClose, true);
      window.addEventListener('resize', handleClose);
    });

    return () => {
      window.cancelAnimationFrame(frame);
      if (subscribed) {
        window.removeEventListener('scroll', handleClose, true);
        window.removeEventListener('resize', handleClose);
      }
    };
  }, [isOpen, handleClose]);

  const menu = (
    <div
      className="dropdown-menu"
      ref={menuRef}
      style={{
        top: `${menuPosition.top}px`,
        left: `${menuPosition.left}px`
      }}
    >
      {items.map((item, index) => (
        <button
          key={item.id}
          className={`dropdown-item ${item.variant === 'danger' ? 'danger' : ''} ${index !== items.length - 1 ? 'with-separator' : ''}`}
          onClick={handleItemClick(item)}
        >
          {item.icon && (
            <div className="dropdown-item-icon">
              {item.icon}
            </div>
          )}
          <div className="dropdown-item-text">
            {item.label}
          </div>
        </button>
      ))}
    </div>
  );

  return (
    <div className={`dropdown ${className || ''}`} ref={dropdownRef}>
      <div className="dropdown-trigger" ref={triggerRef} onClick={handleTriggerClick}>
        {trigger}
      </div>

      {isOpen && createPortal(menu, document.body)}
    </div>
  );
};

export default Dropdown;